import { useContext } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import { Breadcrumbs } from 'types/breadcrumbs';

import Navlink from './atoms/navlink/Navlink';
import RoundedImage from './atoms/rounded-image/RoundedImage';
import AuthApi from '../api/authApi';
import genericPlatformLogo from '../assets/images/generic_platform_logo.svg';
import kthLogo from '../assets/images/KTH_Logotyp_sm.jpg';
import { AuthContext } from '../utils/AuthProvider';
import { useIsModerator, useProfileImageUrl } from '../utils/hooks';

import './Navbar.scss';
import NotificationButton from './molecules/notification/NotificationButton';
import IconButton from './atoms/button/IconButton';

// Each navlink should have a unique key.
export type NavlinkKey = 'home' | 'my-creations';

interface NavbarProps {
  activeNavlink?: NavlinkKey;
  breadcrumbs?: Breadcrumbs;
}

export default function Navbar({ activeNavlink, breadcrumbs }: NavbarProps) {
  const { user, userProfile } = useContext(AuthContext)!;
  let navigate = useNavigate();

  const profileImage = useProfileImageUrl(userProfile?.imageStoragePath);
  const isModerator = useIsModerator(user);

  async function signOut() {
    await AuthApi.signOut();
    navigate('/');
  }

  const Breadcrumbs = breadcrumbs?.map(([to, label], i) => {
    return (
      // We can use index as keys since breadcrumbs don't change position.
      <li key={i} className="breadcrumb">
        {i < breadcrumbs.length - 1 ? <Link to={to}>{label}</Link> : label}
      </li>
    );
  });

  return (
    <nav className="navbar">
      <div className="content">
        <div className="left-container">
          <Link to={'/'} className="logo">
            <img src={kthLogo} alt="Logo" className="logo" />
          </Link>
          <img src={genericPlatformLogo} alt="Platform title" className="title" />
          {Breadcrumbs && <ol className="breadcrumbs ml-16">{Breadcrumbs}</ol>}
        </div>

        <div className="end-items-container">
          {isModerator && (
            <Navlink
              className="mt-3"
              to="/my-creations"
              icon="material-symbols:dashboard-customize-outline"
              label="My Creations"
              active={activeNavlink === 'my-creations'}
            />
          )}
          {user ? (
            <>
              <NotificationButton user={user} />
              <Link to={'/profile'}>
                {profileImage && <RoundedImage size="xs" src={profileImage} alt="User profile" />}
              </Link>
              {/* id is needed to make button selectable in e2e test */}
              <IconButton id="signout-button" visualType="no-bg" icon="ic:outline-logout" onClick={signOut} />
            </>
          ) : (
            <>
              <Link to="/signin" className="log-in-link">
                Sign In
              </Link>
              <Link to="/register" className="sign-up-link">
                Register
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
